import { AlertCircle, Check, ChevronDown, Copy, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { BrunixResponse } from '../components/BrunixResponse';
import { SystemMessage } from '../components/SystemMessage';
import { UserQuery } from '../components/UserQuery';
import ConfirmationCard from './ConfirmationCard';
import ElapsedTimer from './ElapsedTimer';
import ExecutorPanel, { PlaybookCard, TemplateCard } from './ExecutorPanel';
import MessageBubble from './MessageBubble';
import PipelineStepsList from './PipelineStepsList';
import RetryCard from './RetryCard';
import { BrunixMessageListProps } from './types';
import { fmtId, fmtTool, formatElapsed } from './utils';

export default function BrunixMessageList({
  messages,
  isStreaming,
  streamingMsgId,
  llmCalling,
  streamJustFinished,
  isTyping = false,
  onConfirmAction,
  onSkipAction,
  onRetryAction,
  onCancelStream,
  onFeedback,
  isCompact = false,
  searchQuery = '',
  currentMatchIndex = -1,
  lastStepReached = false,
  slotInputs: propSlotInputs,
  setSlotInputs: propSetSlotInputs,
  stepSelections: propStepSelections,
  setStepSelections: propSetStepSelections,
  retryArgsText: propRetryArgsText,
  setRetryArgsText: propSetRetryArgsText
}: BrunixMessageListProps) {
  const [collapsedAgents, setCollapsedAgents] = useState<Set<string>>(new Set());
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [revealedMessages, setRevealedMessages] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [localSlotInputs, setLocalSlotInputs] = useState<Record<string, any>>({});
  const slotInputs = propSlotInputs ?? localSlotInputs;
  const setSlotInputs = propSetSlotInputs ?? setLocalSlotInputs;

  const [localRetryArgsText, setLocalRetryArgsText] = useState<Record<string, string>>({});
  const retryArgsText = propRetryArgsText ?? localRetryArgsText;
  const setRetryArgsText = propSetRetryArgsText ?? setLocalRetryArgsText;

  useEffect(() => {
    if (streamJustFinished) {
      setCollapsedAgents(prev => {
        const next = new Set(prev);
        next.add(streamJustFinished);
        return next;
      });
      setRevealedMessages(prev => {
        const next = new Set(prev);
        next.add(streamJustFinished);
        return next;
      });
    }
  }, [streamJustFinished]);

  // Reveal any assistant message that isn't the one actively streaming right now
  // (e.g. messages loaded from history, or restored after a remount).
  useEffect(() => {
    setRevealedMessages(prev => {
      let next: Set<string> | null = null;
      for (const m of messages) {
        if (m.role === 'assistant' && m.id !== streamingMsgId && !prev.has(m.id)) {
          if (!next) next = new Set(prev);
          next.add(m.id);
        }
      }
      return next ?? prev;
    });
  }, [messages, streamingMsgId]);

  const toggleAgentPanel = (id: string) => {
    setCollapsedAgents(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleConfirmAction = onConfirmAction || (() => { });
  const handleSkipAction = onSkipAction || (() => { });
  const handleRetryAction = onRetryAction || (() => { });
  const handleFeedback = onFeedback || (() => { });

  return (
    <div className={`bx-messages bx-message-list-container ${isCompact ? 'compact-mode' : ''}`}>
      {messages.map(msg => (
        <div key={msg.id} className={`bx-msg bx-msg--${msg.role}`}>
          {msg.parts ? (
            <div className="bx-steps-container">
              {msg.parts.map((part, i) => {
                const nextPart = msg.parts![i + 1];
                const isLastStep = part.type === 'step' && i === msg.parts!.length - 1;
                const isLastStepPart = part.type === 'step' && !msg.parts!.slice(i + 1).some(p => p.type === 'step');
                const isActiveStep = isLastStepPart && isStreaming && !nextPart;

                if (part.type === 'tool_result') {
                  return (
                    <div key={i} className="bx-terminal">
                      {(part.toolResults ?? []).map((r: any, j: number) => (
                        <div key={j} className="bx-terminal-block">
                          <div className="bx-terminal-cmd">$ {r.tool}</div>
                          <div className={`bx-terminal-status${r.status >= 400 ? ' bx-terminal-status--error' : ' bx-terminal-status--ok'}`}>
                            Status: {r.status}
                          </div>
                          {r.error && <div className="bx-terminal-error">{r.error}</div>}
                          {r.output != null && (
                            <div className="bx-terminal-output">
                              {typeof r.output === 'string' ? r.output : JSON.stringify(r.output, null, 2)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                }

                if (part.type === 'classify_result') {
                  const CL_LABEL: Record<string, string> = {
                    API_ACTION: 'API Action', RETRIEVAL: 'Knowledge Query',
                    CODE_GENERATION: 'Code Generation', CONVERSATIONAL: 'Conversational',
                    PLATFORM: 'Platform', MCP_ACTION: 'MCP Action',
                  };
                  const CL_COLOR: Record<string, string> = {
                    API_ACTION: '#f87171', RETRIEVAL: '#4e9af1',
                    CODE_GENERATION: '#a78bfa', CONVERSATIONAL: '#6ee7b7',
                    PLATFORM: '#fbbf24', MCP_ACTION: '#fb923c',
                  };
                  const clIntent = part.classifyIntent ?? '';
                  return (
                    <BrunixResponse key={i} label="Brunix_Agent" hideLabel={true} noBubble={true}>
                      <div className="bx-classify-card">
                        <div className="bx-classify-icon">◎</div>
                        <div className="bx-classify-body">
                          <span className="bx-classify-label">Intent classified</span>
                          <span
                            className="bx-classify-badge"
                            style={{ background: CL_COLOR[clIntent] ?? 'rgba(255,255,255,0.15)', marginInline: '6px' }}
                          >
                            {CL_LABEL[clIntent] ?? clIntent}
                          </span>
                          {part.classifyMethod && (
                            <span className="bx-classify-method">via {part.classifyMethod}</span>
                          )}
                        </div>
                      </div>
                    </BrunixResponse>
                  );
                }

                if (part.type === 'plan_ready') {
                  const NODE_LABEL: Record<string, string> = {
                    action: 'action', check: 'check', conditional: 'if', followup: 'then',
                  };
                  const NODE_COLOR: Record<string, string> = {
                    action: '#f87171', check: '#fbbf24', conditional: '#4e9af1', followup: '#6ee7b7',
                  };
                  return (
                    <BrunixResponse key={i} label="Brunix_Agent" hideLabel={true} noBubble={true}>
                      <div className="bx-task-graph-card">
                        <div className="bx-inform-header">
                          <span style={{ fontSize: 11 }}>⚡</span>
                          <span>Plan Ready</span>
                          {part.processId && (
                            <span className="bx-classify-method">{part.processId.replace(/_/g, ' ')}</span>
                          )}
                        </div>
                        <ol className="bx-task-graph-list">
                          {(part.planSteps ?? []).map((s: any, j: number) => (
                            <li key={j} className="bx-task-graph-item">
                              <span
                                className="bx-task-graph-badge"
                                style={{ backgroundColor: NODE_COLOR[s.node_type] ?? '#888' }}
                              >
                                {NODE_LABEL[s.node_type] ?? s.node_type}
                              </span>
                              <span className="bx-task-graph-desc">{s.tool}</span>
                              {s.reason && (
                                <span className="bx-task-graph-dep">{s.reason}</span>
                              )}
                            </li>
                          ))}
                        </ol>
                      </div>
                    </BrunixResponse>
                  );
                }

                if (part.type === 'task_graph') {
                  const TYPE_LABEL: Record<string, string> = {
                    RETRIEVAL: 'Retrieval', CODE_GENERATION: 'Code', CONVERSATIONAL: 'Chat',
                    PLATFORM: 'Platform', API_ACTION: 'API Action',
                  };
                  const TYPE_COLOR: Record<string, string> = {
                    RETRIEVAL: '#4e9af1', CODE_GENERATION: '#a78bfa', CONVERSATIONAL: '#6ee7b7',
                    PLATFORM: '#fbbf24', API_ACTION: '#f87171',
                  };
                  return (
                    <BrunixResponse key={i} label="Brunix_Agent" hideLabel={true} noBubble={true}>
                      <div className="bx-task-graph-card">
                        <div className="bx-inform-header">
                          <AlertCircle size={12} />
                          <span>Task Decomposition</span>
                        </div>
                        <ol className="bx-task-graph-list">
                          {(part.taskGraph ?? []).map((t: any, j: number) => (
                            <li key={j} className="bx-task-graph-item">
                              <span
                                className="bx-task-graph-badge"
                                style={{ backgroundColor: TYPE_COLOR[t.type] ?? '#888' }}
                              >
                                {TYPE_LABEL[t.type] ?? t.type}
                              </span>
                              <span className="bx-task-graph-desc">{typeof t.task === 'string' ? t.task : JSON.stringify(t.task)}</span>
                              {(t.depends_on?.length ?? 0) > 0 && (
                                <span className="bx-task-graph-dep">
                                  after {t.depends_on.map((d: number) => `#${d + 1}`).join(', ')}
                                </span>
                              )}
                            </li>
                          ))}
                        </ol>
                      </div>
                    </BrunixResponse>
                  );
                }

                if (part.type === 'tools_loaded') {
                  return (
                    <div key={i} className="bx-eval-step">
                      <span className="bx-eval-icon">⚙</span>
                      <span className="bx-eval-label">{part.toolsCount} tools loaded</span>
                    </div>
                  );
                }

                if (part.type === 'playbook_found') {
                  const execSteps = msg.parts!.slice(i + 1).filter(p => p.type === 'step_progress' && p.stepProgress?.status !== 'start');
                  const stillStreaming = isStreaming && streamingMsgId === msg.id;
                  if (stillStreaming || execSteps.length === 0) {
                    return <PlaybookCard key={i} processId={part.processId} />;
                  }
                  const execKey = `exec-${msg.id}-${i}`;
                  return (
                    <ExecutorPanel
                      key={i}
                      title={`EXECUTOR · ${fmtId(part.processId)}`}
                      icon="📖"
                      execSteps={execSteps}
                      isCollapsed={collapsedAgents.has(execKey)}
                      onToggle={() => toggleAgentPanel(execKey)}
                    />
                  );
                }

                if (part.type === 'template_found') {
                  const execSteps = msg.parts!.slice(i + 1).filter(p => p.type === 'step_progress' && p.stepProgress?.status !== 'start');
                  const stillStreaming = isStreaming && streamingMsgId === msg.id;
                  if (stillStreaming || execSteps.length === 0) {
                    return <TemplateCard key={i} processId={part.processId} tools={part.templateTools} />;
                  }
                  const execKey = `exec-${msg.id}-${i}`;
                  return (
                    <ExecutorPanel
                      key={i}
                      title={`EXECUTOR · ${fmtId(part.processId)}`}
                      icon="📋"
                      execSteps={execSteps}
                      isCollapsed={collapsedAgents.has(execKey)}
                      onToggle={() => toggleAgentPanel(execKey)}
                    />
                  );
                }

                if (part.type === 'generating_response') {
                  return (
                    <div key={i} className="bx-eval-step">
                      <span className="bx-eval-icon bx-step-dot--pulse" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#fbbf24', animation: 'bx-pulse 1s ease-in-out infinite' }} />
                      <span className="bx-eval-label">Generating response...</span>
                    </div>
                  );
                }

                if (part.type === 'inform') return null;


                if (part.type === 'confirmation') {
                  if (part.confirmationPayload?.type === 'step_error') {
                    return (
                      <RetryCard
                        key={i}
                        payload={part.confirmationPayload}
                        runId={part.runId!}
                        isPending={part.confirmed === null || part.confirmed === undefined}
                        confirmed={part.confirmed}
                        onConfirmAction={handleConfirmAction}
                        onSkipAction={handleSkipAction}
                        onRetryAction={handleRetryAction}
                        retryArgsText={retryArgsText}
                        setRetryArgsText={setRetryArgsText}
                      />
                    );
                  }
                  return (
                    <ConfirmationCard
                      key={i}
                      payload={part.confirmationPayload}
                      runId={part.runId!}
                      isPending={part.confirmed === null || part.confirmed === undefined}
                      confirmed={part.confirmed}
                      onConfirmAction={handleConfirmAction}
                      slotInputs={slotInputs}
                      setSlotInputs={setSlotInputs}
                    />
                  );
                }

                if (part.type === 'step') {
                  const isCurrentStream = isStreaming && streamingMsgId === msg.id;
                  if (isCurrentStream) {
                    const isDone = !!nextPart;
                    const isFirstStep = i === msg.parts!.findIndex(p => p.type === 'step');
                    return (
                      <React.Fragment key={i}>
                        {isFirstStep && (
                          <BrunixResponse label="Brunix_Agent" noBubble={true} hideLabel={true}>
                            <div className="bx-agents-panel" style={{ marginBottom: '8px' }}>
                              <button className="bx-agents-panel-header" style={{ cursor: 'default' }}>
                                <ChevronDown size={12} className="bx-agents-chevron" style={{ color: 'rgba(255,255,255,0.5)' }} />
                                <span className="bx-agents-title" style={{ color: 'rgba(255,255,255,0.5)', flex: 0 }}>
                                  Thinking
                                </span>
                                <span className="bx-thinking-suffix">
                                  <span className="bx-thinking-dot" />
                                  <span className="bx-thinking-dot" />
                                  <span className="bx-thinking-dot" />
                                </span>
                                {msg.startedAt != null && <ElapsedTimer startedAt={msg.startedAt} />}
                              </button>
                            </div>
                          </BrunixResponse>
                        )}
                        <div className="bx-agents-list-inner-thinking">
                          <div className={`bx-step-row${isActiveStep ? ' bx-step-row--active' : ''}`}>
                            <span className={`bx-step-dot${isActiveStep ? ' bx-step-dot--pulse' : isDone ? ' bx-step-dot--done' : ''}`} />
                            <span className="bx-agent-badge">{part.content}</span>
                            {part.message && (
                              <span className="bx-step-message">{part.message}</span>
                            )}
                            {isActiveStep && (
                              <button
                                title="Cancel process"
                                onClick={() => { onCancelStream?.(); }}
                                style={{
                                  marginLeft: 'auto',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 3,
                                  background: 'transparent',
                                  border: '1px solid rgba(248,113,113,0.35)',
                                  borderRadius: 4,
                                  color: 'rgba(248,113,113,0.7)',
                                  fontSize: 9,
                                  padding: '1px 5px',
                                  cursor: 'pointer',
                                  lineHeight: 1,
                                }}
                              >
                                <X size={8} />
                                cancel
                              </button>
                            )}
                          </div>
                          {nextPart?.type === 'step' && <div className="bx-step-connector" />}

                          {isLastStepPart && msg.parts!.some(p => p.type === 'step_progress') && (() => {
                            const spParts = msg.parts!.filter(p => p.type === 'step_progress');
                            return spParts.map((sp, si) => {
                              const spd = sp.stepProgress;
                              const isRun = !spd || spd.status === 'start';
                              const isOk2 = spd?.status === 'ok';
                              const pSteps = sp.pipelineSteps ?? [];
                              return (
                                <React.Fragment key={si}>
                                  <div className="bx-step-connector" style={{ marginLeft: 16 }} />
                                  <div className="bx-step-row" style={{ paddingLeft: 16 }}>
                                    {isRun ? <span className="bx-step-dot bx-step-dot--pulse" /> : <span className={`bx-step-dot ${isOk2 ? 'bx-step-dot--done' : 'bx-step-dot--error'}`} />}
                                    <span className="bx-agent-badge" style={{ color: isRun ? 'rgba(255,255,255,0.6)' : isOk2 ? '#4ade80' : '#f87171' }}>{spd ? fmtTool(spd.tool) : '...'}</span>
                                    {spd && <span className="bx-step-message">{spd.step}/{spd.total}</span>}
                                  </div>
                                  <PipelineStepsList pipelineSteps={pSteps} stepKeyPrefix={`th-ps-${msg.id}-${si}`} />
                                </React.Fragment>
                              );
                            });
                          })()}

                          {isActiveStep && llmCalling && llmCalling !== 'code_generator' && (
                            <>
                              <div className="bx-step-connector" />
                              <div className="bx-step-row">
                                <span className="bx-step-dot bx-step-dot--pulse" style={{ background: '#fbbf24' }} />
                                <span className="bx-agent-badge" style={{ color: 'rgba(251,191,36,0.9)', fontSize: 10, textTransform: 'lowercase' }}>
                                  {llmCalling === 'slot_resolver' ? 'preparing plan parameters...' :
                                    llmCalling === 'planner' ? 'generating action plan...' :
                                      'calling LLM'}
                                </span>
                              </div>
                            </>
                          )}

                          {!nextPart && (!isStreaming || streamJustFinished === msg.id) && (
                            <>
                              <div className="bx-step-connector" />
                              <div className="bx-step-row bx-step-row--done">
                                <span className="bx-step-dot bx-step-dot--done bx-step-dot--green" />
                                <span className="bx-agent-badge bx-agent-badge--done">Done</span>
                              </div>
                            </>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  }

                  if (i !== msg.parts!.findIndex(p => p.type === 'step')) return null;
                  const allSteps = msg.parts!.filter(p => p.type === 'step');
                  const isCollapsed = collapsedAgents.has(msg.id);

                  return (
                    <BrunixResponse key={`ran-${msg.id}`} label="Brunix_Agent" noBubble={true} hideLabel={true}>
                      <div className="bx-agents-panel">
                        <button
                          className="bx-agents-panel-header"
                          onClick={() => toggleAgentPanel(msg.id)}
                        >
                          <ChevronDown
                            size={12}
                            className={`bx-agents-chevron${isCollapsed ? ' bx-agents-chevron--collapsed' : ''}`}
                          />
                          <span className="bx-agents-title">
                            RAN {allSteps.length} AGENT{allSteps.length !== 1 ? 'S' : ''}
                          </span>
                        </button>

                        {!isCollapsed && (
                          <div className="bx-agents-list-inner">
                            {allSteps.map((s, idx) => {
                              const hasNext = idx < allSteps.length - 1;
                              const stepKey = `${msg.id}-step-${idx}`;
                              const isStepExpanded = expandedSteps.has(stepKey);
                              const toggleStep = () => setExpandedSteps(prev => {
                                const next = new Set(prev);
                                if (next.has(stepKey)) next.delete(stepKey); else next.add(stepKey);
                                return next;
                              });

                              return (
                                <React.Fragment key={idx}>
                                  <div
                                    className="bx-step-row"
                                    style={s.stepData ? { cursor: 'pointer' } : undefined}
                                    onClick={s.stepData ? toggleStep : undefined}
                                  >
                                    <span className="bx-step-dot bx-step-dot--done" />
                                    <span className="bx-agent-badge" style={s.stepData ? { display: 'inline-flex', alignItems: 'center', gap: 4 } : undefined}>
                                      {s.content}
                                      {s.stepData && (
                                        s.stepData.valid === true || s.stepData.complete === true || s.stepData.is_followup === false || s.stepData.gaps === false
                                          ? <Check size={9} style={{ opacity: 0.9, color: '#4ade80', flexShrink: 0 }} />
                                          : <AlertCircle size={9} style={{ opacity: 0.9, color: '#fbbf24', flexShrink: 0 }} />
                                      )}
                                    </span>
                                    {s.stepData && (
                                      <ChevronDown size={10} style={{ marginLeft: 2, opacity: 0.7, color: 'rgba(255,255,255,0.6)', transform: isStepExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                                    )}
                                    {s.message && <span className="bx-step-message">{s.message}</span>}
                                  </div>
                                  {s.stepData && isStepExpanded && (
                                    <div className="bx-terminal-output bx-step-output-scroll" style={{ margin: '4px 0 4px 16px', fontSize: 11 }}>
                                      {s.content === 'analyze_followup'
                                        ? (s.stepData.is_followup
                                          ? `Continuation of [${s.stepData.prev_type ?? '?'}]`
                                          : 'New topic — clean context')
                                        : s.content === 'validate_avap'
                                          ? (s.stepData.valid === true ? 'Parser: OK' : 'Parser: error')
                                          : s.content === 'check_compile_context'
                                            ? (s.stepData.gaps
                                              ? 'Missing information:\n' + s.stepData.gaps
                                              : 'Complete context')
                                            : s.content === 'check_avap_completeness'
                                              ? (s.stepData.complete === true
                                                ? (s.stepData.forced ? 'Coverage: OK (max retries reached)' : 'Coverage: OK')
                                                : 'Missing:\n' + (s.stepData.missing ?? []).join('\n'))
                                              : s.content === 'run_avap'
                                                ? (() => {
                                                  const parts: string[] = [];
                                                  if (s.stepData.test_inputs && Object.keys(s.stepData.test_inputs).length > 0)
                                                    parts.push('Test inputs:\n' + JSON.stringify(s.stepData.test_inputs, null, 2));
                                                  if (s.stepData.test_list?.length > 0)
                                                    parts.push('Assertions:\n' + s.stepData.test_list.join('\n'));
                                                  const t = s.stepData.trace;
                                                  if (t) {
                                                    try { parts.push('Result:\n' + JSON.stringify(JSON.parse(t), null, 2)); }
                                                    catch { parts.push('Result:\n' + t); }
                                                  } else {
                                                    parts.push(s.stepData.valid === true ? 'Execution: OK' : 'Execution: error');
                                                  }
                                                  return parts.join('\n\n');
                                                })()
                                                : s.content === 'syntax_validation'
                                                  ? (() => {
                                                    const lines: string[] = [];
                                                    if (s.stepData.valid === true) {
                                                      lines.push('Syntax: OK');
                                                    } else {
                                                      lines.push('Errors:\n' + (s.stepData.errors ?? []).join('\n'));
                                                    }
                                                    if (s.stepData.ast) lines.push('\nAST:\n' + s.stepData.ast);
                                                    return lines.join('\n');
                                                  })()
                                                  : (typeof s.content === 'string' && s.content.startsWith('solving_issues'))
                                                    ? (() => {
                                                      const lines: string[] = [`Attempt: ${s.stepData.attempt ?? '?'}`];
                                                      if (s.stepData.reason) lines.push('\nReason:\n' + s.stepData.reason);
                                                      if (s.stepData.bytecode) lines.push('\nAST:\n' + JSON.stringify(s.stepData.bytecode, null, 2));
                                                      return lines.join('\n');
                                                    })()
                                                    : JSON.stringify(s.stepData, null, 2)}
                                    </div>
                                  )}
                                  {hasNext && <div className="bx-step-connector" />}

                                  {!hasNext && msg.parts!.some(p => p.type === 'step_progress') && (() => {
                                    const _sp2 = msg.parts!.filter(p => p.type === 'step_progress' && p.stepProgress?.status !== 'start');
                                    return _sp2.map((sp, si) => {
                                      const spd = sp.stepProgress;
                                      const isOk2 = spd?.status === 'ok';
                                      const pSteps = sp.pipelineSteps ?? [];
                                      const spKey2 = `${msg.id}-sp-${si}`;
                                      const isSpExp = expandedSteps.has(spKey2);
                                      const toggleSp = () => setExpandedSteps(prev => { const n = new Set(prev); n.has(spKey2) ? n.delete(spKey2) : n.add(spKey2); return n; });
                                      const avapCode2 = spd?.output?.avap_code ?? null;
                                      const commitId2 = spd?.output?.commit_id ?? null;
                                      return (
                                        <React.Fragment key={si}>
                                          <div className="bx-step-connector" style={{ marginLeft: 16 }} />
                                          <div className="bx-step-row" style={{ paddingLeft: 16, cursor: 'pointer' }} onClick={toggleSp}>
                                            <span className={`bx-step-dot ${isOk2 ? 'bx-step-dot--done' : 'bx-step-dot--error'}`} />
                                            <span className="bx-agent-badge" style={{ color: isOk2 ? '#4ade80' : '#f87171' }}>{spd ? fmtTool(spd.tool) : '...'}</span>
                                            {spd && <span className="bx-step-message">{spd.step}/{spd.total}</span>}
                                            <ChevronDown size={9} style={{ marginLeft: 'auto', opacity: 0.4, transform: isSpExp ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }} />
                                          </div>
                                          <PipelineStepsList pipelineSteps={pSteps} stepKeyPrefix={`ran-ps-${msg.id}-${si}`} />
                                          {isSpExp && avapCode2 && (
                                            <div className="bx-terminal-output bx-step-output-scroll bx-terminal-avap-code" style={{ margin: '2px 0 4px 32px', fontSize: 10 }}>
                                              {avapCode2}
                                              {commitId2 && <div style={{ color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>commit: {commitId2}</div>}
                                            </div>
                                          )}
                                        </React.Fragment>
                                      );
                                    });
                                  })()}
                                </React.Fragment>
                              );
                            })}
                            <div className="bx-step-connector" />
                            <div className="bx-step-row bx-step-row--done">
                              <span className="bx-step-dot bx-step-dot--done bx-step-dot--green" />
                              <span className="bx-agent-badge bx-agent-badge--done">Done</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </BrunixResponse>
                  );
                }

                if (part.type === 'step_progress') {
                  const hasExecutorHeader = msg.parts!.slice(0, i).some(
                    p => p.type === 'playbook_found' || p.type === 'template_found'
                  );
                  if (hasExecutorHeader) return null;

                  const hasStepParts = msg.parts!.some(p => p.type === 'step');
                  if (hasStepParts) return null;

                  const allCompleted = msg.parts!.filter(
                    p => p.type === 'step_progress' && p.stepProgress?.status !== 'start'
                  );
                  const isFirstCompleted = allCompleted[0] === part;
                  const stillStreaming = isStreaming && streamingMsgId === msg.id;

                  if (stillStreaming) {
                    const allStreamingSteps = msg.parts!.filter(p => p.type === 'step_progress');
                    if (allStreamingSteps[0] !== part) return null;
                    const execKey = `exec-confirm-${msg.id}`;
                    return (
                      <ExecutorPanel
                        key={i}
                        title="EXECUTOR"
                        icon="⚙"
                        execSteps={allStreamingSteps}
                        isCollapsed={collapsedAgents.has(execKey)}
                        onToggle={() => toggleAgentPanel(execKey)}
                      />
                    );
                  }

                  if (!isFirstCompleted) return null;

                  const execKey = `exec-confirm-${msg.id}`;
                  return (
                    <ExecutorPanel
                      key={i}
                      title={`EXECUTOR · ${allCompleted.length} TOOL${allCompleted.length !== 1 ? 'S' : ''}`}
                      icon="⚙"
                      execSteps={allCompleted}
                      isCollapsed={collapsedAgents.has(execKey)}
                      onToggle={() => toggleAgentPanel(execKey)}
                    />
                  );
                }

                if (part.type === 'cancelled') {
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 8px', color: 'rgba(248,113,113,0.55)', fontSize: 10 }}>
                      <X size={9} />
                      Cancelled by user
                    </div>
                  );
                }

                if (!part.content) return null;
                if (!revealedMessages.has(msg.id) && streamingMsgId !== msg.id) return null;

                const BubbleComponent = msg.role === 'user' ? UserQuery : msg.role === 'system' ? SystemMessage : BrunixResponse;
                return (
                  <div key={i} className="bx-step-text-part">
                    <BubbleComponent>
                      <MessageBubble
                        content={part.content}
                        messageId={msg.id}
                        searchQuery={searchQuery}
                        currentMatchIndex={currentMatchIndex}
                      />
                    </BubbleComponent>
                  </div>
                );
              })}
            </div>
          ) : (
            (() => {
              const BubbleComponent = msg.role === 'user' ? UserQuery : msg.role === 'system' ? SystemMessage : BrunixResponse;
              return (
                <BubbleComponent>
                  <MessageBubble
                    content={msg.content}
                    messageId={msg.id}
                    searchQuery={searchQuery}
                    currentMatchIndex={currentMatchIndex}
                  />
                </BubbleComponent>
              );
            })()
          )}
          <div className="bx-msg-meta" style={{
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            alignItems: 'center',
            gap: '8px'
          }}>
            {msg.role === 'assistant' && (
              <div className="bx-msg-actions">
                <button className="bx-msg-action-btn" onClick={() => copyToClipboard(msg.parts ? msg.parts.filter((p: any) => p.type === 'text').map((p: any) => p.content).join('') : msg.content, msg.id)}>
                  {copiedId === msg.id ? <Check size={12} className="bx-check-green" /> : <Copy size={12} />}
                </button>
                <button className={`bx-msg-action-btn${msg.feedback === 'like' ? ' bx-msg-action-btn--liked' : ''}`} onClick={() => handleFeedback(msg.id, 'like')} title="Like"><ThumbsUp size={12} /></button>
                <button className={`bx-msg-action-btn${msg.feedback === 'dislike' ? ' bx-msg-action-btn--disliked' : ''}`} onClick={() => handleFeedback(msg.id, 'dislike')} title="Dislike"><ThumbsDown size={12} /></button>
              </div>
            )}
            {msg.role !== 'system' && (
              <span className="bx-msg-time">
                {msg.role === 'assistant' && msg.elapsedMs != null && (
                  <span className="bx-msg-elapsed" title="Total response time">{formatElapsed(msg.elapsedMs)} · </span>
                )}
                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>
      ))}

      {isTyping && streamingMsgId === null && messages.some(m => m.role === 'user') && (
        <BrunixResponse label="Brunix_Agent" noBubble={true} hideLabel={true}>
          <div className="bx-agents-panel">
            <button className="bx-agents-panel-header" style={{ cursor: 'default' }}>
              <ChevronDown size={12} className="bx-agents-chevron" style={{ color: 'rgba(255,255,255,0.5)', flex: 0 }} />
              <span className="bx-agents-title" style={{ color: 'rgba(255,255,255,0.5)' }}>Thinking</span>
              <span className="bx-thinking-suffix">
                <span className="bx-thinking-dot" />
                <span className="bx-thinking-dot" />
                <span className="bx-thinking-dot" />
              </span>
            </button>
          </div>
        </BrunixResponse>
      )}
    </div>
  );
}

